import { Box, Button, Combobox, Flex, Group, Text, useCombobox } from '@mantine/core';
import { CheckIcon, GlobeSimpleIcon } from '@phosphor-icons/react';
import type { FC } from 'react';

import { localeToEmoji } from '../../../lib/localeToEmoji.ts';

interface Language {
  code?: string | null;
  name?: string | null;
  direction?: string | null;
}

export interface LanguagesDropdownProps {
  languages: Language[];
  activeLocale: string;
  setActiveLocale: (activeLocale: string) => void;
}

export const LanguagesDropdown: FC<LanguagesDropdownProps> = ({ languages, activeLocale, setActiveLocale }) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const options = languages.map((language) => (
    <Combobox.Option key={language.code} value={language.code ?? 'UNKNOWN'}>
      <Flex align="center" justify="space-between">
        <Flex align="center" gap="xs">
          <Box>{language.code && localeToEmoji(language.code)}</Box>
          <Text fw={activeLocale === language.code ? 800 : 500} size="sm">
            {language.name}
          </Text>
        </Flex>

        {activeLocale === language.code && <CheckIcon size={20} />}
      </Flex>
    </Combobox.Option>
  ));

  return (
    <Combobox
      withArrow
      position="bottom"
      store={combobox}
      width="220"
      onOptionSubmit={(val) => {
        const selectedLanguage = languages.find((language) => language.code === val);

        if (!selectedLanguage) {
          return;
        }

        setActiveLocale(selectedLanguage.code ?? 'UNKNOWN');
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <Button color="dark.3" size="lg" type="button" variant="subtle" onClick={() => combobox.toggleDropdown()}>
          <Group gap="xxxs">
            <GlobeSimpleIcon size="28" />
            <Text size="xs">{languages.find((language) => language.code === activeLocale)?.name}</Text>
          </Group>
        </Button>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
